import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

// Utils
import { useUniqueId } from 'src/hooks'

// Components
import { Dropdown } from 'src/Components'
import { ArrowIcon } from './ArrowIcon'

// Styles
import styles from './DropdownSelect.module.scss'

const dropdownSelectPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  dataqa: PropTypes.string,
  disabled: PropTypes.bool,
  dropdownClassName: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  primaryActionClassName: PropTypes.string,
  selectedDropdownItem: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
}

const dropdownSelectDefaultProps = {
  size: 'medium',
}

const DropdownSelect = ({
  children,
  dataqa,
  disabled,
  dropdownClassName,
  label,
  onClick,
  primaryActionClassName,
  selectedDropdownItem,
  size,
}) => {
  const [open, setOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState(selectedDropdownItem)

  useEffect(() => {
    setSelectedOption(selectedDropdownItem)
  }, [selectedDropdownItem])

  const AnimatedCarrot = useCallback(
    () => (
      <div className={classnames(styles.notSelected, open && styles.selected)}>
        <ArrowIcon />
      </div>
    ),
    [open]
  )

  return (
    <Dropdown
      aria-label='expand home navigation options'
      className='dropdown-select'
      disabled={disabled}
      setOpen={setOpen}
      open={open}
      buttonProps={{
        className: classnames(
          primaryActionClassName,
          styles.dropdownSelectButton
        ),
        label,
        onClick,
        size,
        dataqa,
      }}
      dropdownToggleProps={{
        className: classnames(dropdownClassName, styles.dropdownToggleButton),
        iconEnd: AnimatedCarrot,
      }}
    >
      <DropdownSelectMenu
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        setOpen={setOpen}
      >
        {children}
      </DropdownSelectMenu>
    </Dropdown>
  )
}

const dropdownSelectMenuPropTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  selectedOption: PropTypes.string,
  setSelectedOption: PropTypes.func.isRequired,
  setOpen: PropTypes.func.isRequired,
}

const DropdownSelectMenu = ({
  children,
  selectedOption,
  setSelectedOption,
  setOpen,
}) => {
  const dropdownItemFallBackKey = useUniqueId('dropdownItem_')

  return (
    <ul className={styles.dropdownSelectMenu}>
      {(Array.isArray(children) ? children : [children]).map(child => {
        if (child.type.displayName !== 'DropdownItem') {
          throw new Error('DropdownItem must be used within DropdownSelect')
        }

        const enhancedDropdownItem = React.cloneElement(child, {
          key: child.props.id || dropdownItemFallBackKey,
          selectedOption,
          setSelectedOption,
          setOpen,
        })

        return enhancedDropdownItem
      })}
    </ul>
  )
}

DropdownSelect.defaultProps = dropdownSelectDefaultProps
DropdownSelect.propTypes = dropdownSelectPropTypes
DropdownSelectMenu.propTypes = dropdownSelectMenuPropTypes

export { DropdownSelect, DropdownSelectMenu }
